import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentService } from '@/services/payment/paymentService';
import { CreatePaymentRequest, UpdatePaymentRequest } from '@/types/payment';

export const usePayment = () => {
  const queryClient = useQueryClient();

  // Get single payment with details (joins Invoice and Contract)
  const useGetPayment = (paymentId: string) => useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => PaymentService.getPayment(paymentId),
    enabled: !!paymentId && paymentId.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Get all payments with pagination
  const useGetAllPayments = (page: number = 1, limit: number = 10) => useQuery({
    queryKey: ['payments', 'all', page, limit],
    queryFn: () => PaymentService.getAllPayments(page, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });


  // Create payment mutation
  const createPaymentMutation = useMutation({
    mutationFn: (data: CreatePaymentRequest) => PaymentService.createPayment(data),
    onSuccess: (newPayment) => {
      // Invalidate and refetch payments queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payment', newPayment.id] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Update payment mutation
  const updatePaymentMutation = useMutation({
    mutationFn: (data: UpdatePaymentRequest) => 
      PaymentService.updatePayment(data),
    onSuccess: (updatedPayment) => {
      // Invalidate and refetch payments queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payment', updatedPayment.id] });
      // Invalidate listing queries (both old and new query keys)
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['admin-listing-details'] });
    },
  });

  // Delete payment mutation
  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string) => PaymentService.deletePayment(paymentId),
    onSuccess: (_, paymentId) => {
      // Invalidate and refetch payments queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payment', paymentId] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  return {
    // Queries
    useGetPayment,
    useGetAllPayments,
    
    // Create payment
    createPayment: createPaymentMutation.mutate,
    createPaymentAsync: createPaymentMutation.mutateAsync,
    isCreatingPayment: createPaymentMutation.isPending,
    createPaymentError: createPaymentMutation.error,
    createPaymentSuccess: createPaymentMutation.isSuccess,
    
    // Update payment
    updatePayment: updatePaymentMutation.mutate,
    updatePaymentAsync: updatePaymentMutation.mutateAsync,
    isUpdatingPayment: updatePaymentMutation.isPending,
    updatePaymentError: updatePaymentMutation.error,
    updatePaymentSuccess: updatePaymentMutation.isSuccess,
    
    // Delete payment
    deletePayment: deletePaymentMutation.mutate,
    deletePaymentAsync: deletePaymentMutation.mutateAsync,
    isDeletingPayment: deletePaymentMutation.isPending,
    deletePaymentError: deletePaymentMutation.error,
    deletePaymentSuccess: deletePaymentMutation.isSuccess,
    
    // Reset functions
    resetCreatePayment: createPaymentMutation.reset,
    resetUpdatePayment: updatePaymentMutation.reset,
    resetDeletePayment: deletePaymentMutation.reset,
  };
};
